// No imports on purpose: components with Jest tests use this, and pulling
// axios into a test needs its ESM entry mocked first.

/**
 * Text as a search compares it: lowercased, with Latin accents dropped, so
 * "Brene Brown" finds "Brené Brown" and the other way round (content team,
 * 25 Oct 2026). Only the combining DIACRITICS block (U+0300–U+036F) is
 * removed — Devanagari vowel signs are combining marks too, and stripping
 * every mark would break Hindi. The backend's searches do the same
 * (pen-space-backend `helpers/searchTokens.js`).
 */
export const foldForSearch = (value) =>
  String(value ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "").normalize("NFC").toLowerCase();

/**
 * Whether `text` contains `query`, ignoring case and accents. Same edges as
 * the `.toLowerCase().includes()` it replaced: an empty query matches
 * everything, a missing one nothing, and the query is not trimmed.
 */
export const includesFolded = (text, query) =>
  query === undefined || query === null ? false : foldForSearch(text).includes(foldForSearch(query));

const isWordChar = (ch) => /[\p{L}\p{N}]/u.test(ch);

/**
 * How well `text` matches `term` (already folded), best first: 0 the exact
 * text, 1 starts with it, 2 a word in it starts with it, 3 anywhere else.
 * Alphabetical alone buried "Peak" ninth under "Algospeak" and "Black Elk
 * Speaks" — below the fold of every book picker (content team, 25 Oct 2026).
 */
export const matchRank = (text, term) => {
  const t = foldForSearch(text);
  if (t === term) return 0;
  if (t.startsWith(term)) return 1;
  for (let i = t.indexOf(term, 1); i !== -1; i = t.indexOf(term, i + 1)) {
    if (!isWordChar(t[i - 1])) return 2;
  }
  return 3;
};

/**
 * The items whose text contains `query` (case and accents ignored), best
 * match first — exact, then starts with, then a word starting with it, then
 * the rest. A stable sort, so each group keeps the list's own order.
 */
export const rankMatches = (items, query, getText = (item) => item?.label) => {
  const term = foldForSearch(query).trim();
  if (!term) return items;
  return items
    .filter((item) => foldForSearch(getText(item)).includes(term))
    .map((item) => ({ item, rank: matchRank(getText(item), term) }))
    .sort((a, b) => a.rank - b.rank)
    .map(({ item }) => item);
};
